<template>
  <v-card class="d-flex flex-column" :class="xs ? 'ma-0 pa-0' : 'ma-1 pa-4'" :style="contentStyle">
    <v-card-title v-if="title">{{ title }}</v-card-title>

    <div id="editor-factory" class="d-flex flex-column form-fields scroll-container pb-3">
      <span v-if="!multiplierCell">
        <div v-for="(cell, index) in cellsFiltered" :key="'cell-' + cell.cellName + '-' + index" :style="getWrapperStyle(cell)">
          <component :is="getMappedComponent(cell.cellName)" :cell="cell" :namespace="namespace" class="mt-2 mb-2" />
        </div>
      </span>

      <div v-else>
        <v-card v-for="index in multiplierCount" :key="'m-cell-' + index" class="ma-2 mb-6">
          <v-card-title style="justify-content: space-between">
            {{ title }} {{ index }}
            <v-btn v-if="index === multiplierCount && multiplierCount !== 1" icon @click="$emit('removeMultiplier')">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text>
            <div v-for="(cell, cIndex) in cellsFiltered" :key="'cell-' + cell.cellName + '-' + cIndex" :style="getWrapperStyle(cell)">
              <component
                v-if="!cell.hideOnWebApp"
                :is="getMappedComponent(cell.cellName)"
                :cell="{ ...cell }"
                :mIndex="(index - 1).toString()"
                :namespace="namespace"
                class="mt-2 mb-2"
              />
            </div>
          </v-card-text>
        </v-card>
      </div>
    </div>
    <v-card-actions>
      <slot name="card-actions" />
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapState } from 'vuex';
import common from '@/mixins/common';
import { getTitle, conditionFilter } from './wb-common';
import WBKlassifizierungCell from './WBKlassifizierungCell.vue';
import WBUDPaintCell from './WBUDPaintCell.vue';
import WBUDMediaCell from './WBUDMediaCell.vue';
import UDAudioCell from './UDAudioCell.vue';
import UDRadioCell from './UDRadioCell.vue';
import WBUDSwitchCell from './WBUDSwitchCell.vue';
import UDEigenschadenFahrgastCell from './UDEigenschadenFahrgastCell.vue';
import UDSwitchTextFieldCell from './UDSwitchTextFieldCell.vue';
import WBUDDateCell from './WBUDDateCell.vue';
import UDFahrzeugCell from './UDFahrzeugCell.vue';
import UDSignatureCell from './UDSignatureCell.vue';
import WBUDTimeCell from './WBUDTimeCell.vue';
import WBReferenceCell from './WBReferenceCell.vue';
import WBUDTextViewCell from './WBUDTextViewCell.vue';
import WBUDTextCell from './WBUDTextCell.vue';
import WBUDRadioCell from './WBUDRadioCell.vue';
import WBVideoCell from './WBVideoCell.vue';
import UDHtmlCell from './UDHtmlCell.vue';

export default {
  name: 'WBUDEditorFactory',
  mixins: [common],
  props: {
    cells: { type: Array, required: true },
    title: String,
    namespace: { type: String, required: true },
    multiplierCell: { type: Boolean, default: false },
    multiplierCount: { type: Number, default: 1 },
  },
  components: {
    WBKlassifizierungCell,
    WBUDPaintCell,
    WBUDMediaCell,
    UDAudioCell,
    UDRadioCell,
    WBUDSwitchCell,
    UDEigenschadenFahrgastCell,
    UDSwitchTextFieldCell,
    WBUDDateCell,
    UDFahrzeugCell,
    UDSignatureCell,
    WBUDTimeCell,
    WBReferenceCell,
    WBUDTextViewCell,
    WBUDTextCell,
    WBUDRadioCell,
    WBVideoCell,
    UDHtmlCell,
  },
  data() {
    return {
      getTitle,
    };
  },
  destroyed() {
    if (this.$store.state[this.namespace].dataLoaded) {
      //Only reset data if data is loaded - means user has exited form
      this.$store.state[this.namespace].formInput = {};
    }
  },
  computed: {
    ...mapState({
      klassifizierungen(state) {
        return state[this.namespace].klassifizierungen;
      },
      wbConfig(state) {
        return state[this.namespace].wbConfig;
      },
      navigationItems(state) {
        return state[this.namespace].navigationItems;
      },
    }),

    contentStyle() {
      let style = {};

      if (this.xs) {
        style.height = '100%';
      } else {
        style.height = `calc(100% - 8px)`;
      }
      // style.overflow = 'auto';

      return style;
    },
    cellsFiltered() {
      return this.cells.filter((cell) => conditionFilter(this.input, cell)).filter((cell) => !cell.hideOnWebApp);
    },
    input: {
      get() {
        return this.$store.state[this.namespace].formInput;
      },
      set(val) {
        this.$store.state[this.namespace].formInput = val;
      },
    },
  },
  methods: {
    getMappedComponent(cellName) {
      switch (cellName) {
        case 'WBKlassifizierungCell':
          return WBKlassifizierungCell;
        case 'WBUDPaintCell':
        case 'WBUDBigPaintCell':
        case 'UDFremdFahrzeugPaintCell':
          return WBUDPaintCell;
        case 'WBUDMediaCell':
          return WBUDMediaCell;
        case 'UDAudioCell':
          return UDAudioCell;
        case 'UDRadioCell':
          return UDRadioCell;
        case 'WBUDSwitchCell':
          return WBUDSwitchCell;
        case 'UDEigenschadenFahrgastCell':
          return UDEigenschadenFahrgastCell;
        case 'UDSwitchTextFieldCell':
          return UDSwitchTextFieldCell;
        case 'WBUDDateCell':
        case 'UDDateCell':
          return WBUDDateCell;
        case 'UDFahrzeugCell':
          return UDFahrzeugCell;
        case 'UDSignatureCell':
          return UDSignatureCell;
        case 'WBUDTimeCell':
        case 'UDTimeCell':
          return WBUDTimeCell;
        case 'WBReferenceCell':
          return WBReferenceCell;
        case 'WBUDTextViewCell':
          return WBUDTextViewCell;
        case 'WBUDTextCell':
          return WBUDTextCell;
        case 'WBUDRadioCell':
          return WBUDRadioCell;
        case 'WBVideoCell':
          return WBVideoCell;
        case 'UDHtmlCell':
          return UDHtmlCell;
        default:
          console.warn('No component found for ' + cellName);
      }
    },
    getWrapperStyle(cell) {
      const style = {};
      //style.height = cell.height + 'px'; //Doesn't work for WBKlassifizierungCell, WBUDMediaCell, UDRadioCell, WBUDTextViewCell
      style.alignItems = 'center';
      style.display = 'flex';
      // style.justifyContent='center';
      style.padding = '0 16px';

      return style;
    },
  },
};
</script>

<style scoped>
.card-actions {
  bottom: 0;
  width: 100%;
  justify-content: space-between;
}

.form-fields {
  overflow: auto;
  padding: 0;
  height: 100%;
}
</style>
